import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

import { setSelectedFacility, useAppDispatch } from 'store';

import {
	createFacilitySwitchFollowUp,
	FacilitySwitchFilterContext,
	FacilitySwitchTarget,
	formatFacilitySwitchFollowUp,
	PendingFacilitySwitchFollowUp,
	resolveFacilitySwitchTarget,
} from './facilitySwitchHelpers';

/** Inputs required to coordinate a facility switch and its chat acknowledgement. */
type UseFacilitySwitchArgs = {
	availableFacilities?: Array<FacilitySwitchTarget | undefined>;
	selectedFacility: number | null;
	filtersAreFetching: boolean;
	filterContext: FacilitySwitchFilterContext;
	hasLiveStream: boolean;
	submitKickoffLockRef: MutableRefObject<boolean>;
	onFollowUp: (message: string) => void;
};

/**
 * Coordinates a chat-requested facility switch with Redux and facility-scoped filter
 * refreshes, then submits the internal tool response using the refreshed context.
 */
export function useFacilitySwitch({
	availableFacilities,
	selectedFacility,
	filtersAreFetching,
	filterContext,
	hasLiveStream,
	submitKickoffLockRef,
	onFollowUp,
}: UseFacilitySwitchArgs) {
	const dispatch = useAppDispatch();
	// The acknowledgement remains pending until the selected facility and its filters are ready.
	const [pendingFollowUp, setPendingFollowUp] = useState<PendingFacilitySwitchFollowUp | null>(null);
	const [facilitySwitchError, setFacilitySwitchError] = useState<string | null>(null);
	const refreshGraceTimerRef = useRef<number | null>(null);
	// Timer callbacks read these refs to avoid acting on values captured before a refresh starts.
	const runtimeRef = useRef({ filterContext, filtersAreFetching, hasLiveStream, selectedFacility });
	const onFollowUpRef = useRef(onFollowUp);

	// Keep the latest runtime state and callback available without restarting the timer for callback identity changes.
	runtimeRef.current = { filterContext, filtersAreFetching, hasLiveStream, selectedFacility };
	onFollowUpRef.current = onFollowUp;

	/** Resolves a requested label, selects that facility, and queues its acknowledgement. */
	const handleFacilitySwitch = useCallback(
		(facilityNameOrAlias: string) => {
			// A new command supersedes any pending acknowledgement or previous lookup error.
			setPendingFollowUp(null);
			setFacilitySwitchError(null);
			const targetFacility = resolveFacilitySwitchTarget(facilityNameOrAlias, availableFacilities);

			if (!targetFacility?.id) {
				console.warn('[chat] switch_facility target not found', {
					facilityNameOrAlias,
					availableFacilities,
				});
				setFacilitySwitchError(`Unable to switch to “${facilityNameOrAlias}”. The facility could not be found.`);
				return;
			}

			setPendingFollowUp(createFacilitySwitchFollowUp(targetFacility, facilityNameOrAlias));

			// Repeated requests for the current facility are idempotent, but still need
			// the confirmation turn so the agent can continue the conversation.
			if (targetFacility.id !== selectedFacility) {
				dispatch(setSelectedFacility(targetFacility.id));
			}
		},
		[availableFacilities, dispatch, selectedFacility]
	);

	// Submit only after Redux selection has settled and no filter refresh or chat submission is active.
	useEffect(() => {
		if (!pendingFollowUp) return;
		if (selectedFacility !== pendingFollowUp.targetFacilityId) return;
		if (filtersAreFetching || hasLiveStream || submitKickoffLockRef.current) return;

		const { targetFacilityId } = pendingFollowUp;
		// Give facility-scoped RTK Query hooks one tick to publish `isFetching`.
		// If no request starts, this also acts as the cached-query fallback.
		refreshGraceTimerRef.current = window.setTimeout(() => {
			refreshGraceTimerRef.current = null;
			const runtime = runtimeRef.current;
			if (
				runtime.selectedFacility !== targetFacilityId ||
				runtime.filtersAreFetching ||
				runtime.hasLiveStream ||
				submitKickoffLockRef.current
			) {
				return;
			}

			// Clear first so this acknowledgement cannot be submitted again on the next render.
			setPendingFollowUp(null);
			onFollowUpRef.current(formatFacilitySwitchFollowUp(pendingFollowUp, runtime.filterContext));
		}, 0);

		// Cancel a stale timer whenever readiness inputs change or the hook unmounts.
		return () => {
			if (refreshGraceTimerRef.current !== null) {
				window.clearTimeout(refreshGraceTimerRef.current);
				refreshGraceTimerRef.current = null;
			}
		};
	}, [filtersAreFetching, hasLiveStream, pendingFollowUp, selectedFacility, submitKickoffLockRef]);

	return {
		handleFacilitySwitch,
		facilitySwitchError,
		isFacilitySwitchPending: pendingFollowUp !== null,
	};
}
